import React, { useEffect, useState } from 'react';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Button from 'flow/components/ui-components/Button';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    acceptCookies,
    rejectCookies,
    saveConsentCategories,
} from 'flow/actions/app-actions';
import { selectConsentCategories } from 'flow/selectors/app-selectors';
import { ConsentCategories, categoriesFromRedux } from 'flow/types/consent';
import TrackingConsentOverview from './TrackingConsentOverview';
import TrackingConsentCustomise from './TrackingConsentCustomise';

interface Props {
    open: boolean;
    onClose: () => void;
    onDismiss: () => void;
}

const TrackingConsentModal = ({ open, onClose, onDismiss }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const consentCategories = useAppSelector(selectConsentCategories);
    const [screen, setScreen] = useState<'overview' | 'customise'>('overview');
    const [categories, setCategories] = useState<ConsentCategories>(() =>
        categoriesFromRedux(consentCategories),
    );

    useEffect(() => {
        if (open) {
            setCategories(categoriesFromRedux(consentCategories));
        }
    }, [open, consentCategories]);

    const handleAcceptAll = () => {
        dispatch(acceptCookies());
        onDismiss();
    };

    const handleRejectAll = () => {
        dispatch(rejectCookies());
        onDismiss();
    };

    const handleCategoryChange = (
        key: keyof ConsentCategories,
        value: boolean,
    ) => setCategories((prev) => ({ ...prev, [key]: value }));

    const handleAcceptSelected = () => {
        if (Object.values(categories).every(Boolean)) {
            dispatch(acceptCookies());
        } else if (Object.values(categories).every((v) => !v)) {
            dispatch(rejectCookies());
        } else {
            dispatch(saveConsentCategories(categories));
        }
        onDismiss();
    };

    const handleClose = () => {
        setScreen('overview');
        onClose();
    };

    const header =
        screen === 'overview' ? (
            i18next.t('settings.general.tracking.title')
        ) : (
            <div className="flex items-center gap-2">
                <Button
                    iconKey="arrow_back"
                    color="icon"
                    onClick={() => setScreen('overview')}
                    aria-label={i18next.t('Tooltip.Nav.Title.Back')}
                    className="-ms-1"
                />
                <span>{i18next.t('settings.general.tracking.customise')}</span>
            </div>
        );

    return (
        <BaseModal
            open={open}
            onClose={handleClose}
            disableActions
            disablePadding
            disableHeaderForcedLowerCase
            header={header}
            className="h-[620px] [&>header]:h-16 [&>header]:px-6 [&>header>button]:-me-1"
        >
            {screen === 'overview' ? (
                <TrackingConsentOverview
                    onAcceptAll={handleAcceptAll}
                    onRejectAll={handleRejectAll}
                    onCustomise={() => setScreen('customise')}
                />
            ) : (
                <TrackingConsentCustomise
                    categories={categories}
                    onCategoryChange={handleCategoryChange}
                    onAcceptSelected={handleAcceptSelected}
                />
            )}
        </BaseModal>
    );
};

export default TrackingConsentModal;
